/**
 * `v-observe-visibility="{callback, intersection}"` - call back as the element enters and
 * leaves the viewport, so a long list can render only the rows near it.
 *
 * A port of vue-observe-visibility 1, which this replaces. The library did not merely warn
 * under vue 3 - it threw, on every mount:
 *
 *   TypeError: Cannot read properties of undefined (reading '$nextTick')
 *       at VisibilityState.createObserver
 *       at bind
 *       at invokeDirectiveHook
 *       at mountElement
 *
 * It built the IntersectionObserver in `bind` and then deferred the `observe()` call -
 * "wait for the element to be in document" - through `vnode.context.$nextTick`. Vue 2's
 * `vnode.context` was the instance that rendered the vnode; vue 3 vnodes have no `context`
 * at all, so the read threw before anything was ever observed. The throw comes out of
 * `callWithAsyncErrorHandling` as an unhandled rejection rather than a broken render, which
 * is why the page looked merely empty instead of crashed: the observer existed, nothing was
 * ever observed, the callback never fired, and the three call sites all gate their content
 * on a flag that starts `false` - DiaryPageDiaryEntry and GoalTask render nothing, and
 * DiaryDayLocationContent never starts its load.
 *
 * The deferral has nothing to replace it here. `bind` is `beforeMount`, which is where the
 * element is not in the document yet; a vue 3 `mounted` hook runs as a post-render effect,
 * by which time it is - so this observes straight away and needs no instance to borrow a
 * `$nextTick` from.
 *
 * Kept from the library, because the call sites depend on it:
 *
 *   - `updated` compares the new options against the old and does nothing when they match.
 *     This is load-bearing, not an optimisation: the three call sites pass an object
 *     literal, so the binding value is a fresh object on every single render, and rebuilding
 *     the observer re-reports visibility. The callbacks assign to the flag their own
 *     template reads, so a rebuild-per-render would re-enter through its own re-render.
 *   - visibility is only reported when it *changes* (`oldResult`), for the same reason.
 *   - with several entries in one callback, an intersecting one wins over the first, since
 *     a browser can report the same element twice as it crosses the margin.
 *
 * Dropped, because nothing here used them: `throttle` and `throttleOptions`. `once` is kept
 * - it is four lines and it is the natural thing to reach for next in a lazy-render list.
 */
const CTX = Symbol('observeVisibility');

/**
 * The library's own comparison, which walked only the left operand's keys - so it called
 * `{a: 1}` equal to `{a: 1, b: 2}`, and every non-object unequal value equal as well. This
 * one is symmetric. Functions compare by identity, which is what makes the whole thing work:
 * `callback: visibilityChanged` resolves to the same bound method on every render.
 */
function deepEqual (a, b) {
    if (a === b) {
        return true;
    }
    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
        return false;
    }
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length
        && keys.every(key => deepEqual(a[key], b[key]));
}

const optionsOf = value => (typeof value === 'function' ? { callback: value } : value);

const thresholdOf = options => (typeof options.intersection?.threshold === 'number'
    ? options.intersection.threshold
    : 0);

function observe (el, value) {
    const options = optionsOf(value);
    const context = { observer: null, lastResult: undefined };

    context.observer = new IntersectionObserver(entries => {
        // a browser can report the same element more than once as it crosses the margin,
        // and the intersecting entry is the interesting one whatever order they arrive in
        const entry = entries.length > 1
            ? (entries.find(it => it.isIntersecting) || entries[0])
            : entries[0];

        // `isIntersecting` rather than the ratio alone: something entering very slowly is
        // reported as intersecting with a ratio still at 0
        const result = entry.isIntersecting && entry.intersectionRatio >= thresholdOf(options);
        if (result === context.lastResult) {
            return;
        }
        context.lastResult = result;
        options.callback(result, entry);

        if (result && options.once) {
            context.frozen = true;
            disconnect(context);
        }
    }, options.intersection);

    // `mounted` is a post-render effect, so the element is in the document already - this
    // is what the library needed `vnode.context.$nextTick` for, and what vue 3 took away
    context.observer.observe(el);
    return context;
}

function disconnect (context) {
    if (context?.observer) {
        context.observer.disconnect();
        context.observer = null;
    }
}

function start (el, value) {
    if (!value) {
        return;
    }
    if (typeof IntersectionObserver === 'undefined') {
        console.warn('[observe-visibility] this browser has no IntersectionObserver');
        return;
    }
    el[CTX] = observe(el, value);
}

export default {
    mounted (el, binding) {
        start(el, binding.value);
    },

    updated (el, binding) {
        // the call sites pass an object literal, so this is a fresh object every render and
        // the comparison is the only thing standing between them and a rebuilt observer
        if (deepEqual(binding.value, binding.oldValue)) {
            return;
        }
        // `once` has already fired and let go: a re-render must not start it again
        if (el[CTX]?.frozen) {
            return;
        }
        disconnect(el[CTX]);
        delete el[CTX];
        start(el, binding.value);
    },

    unmounted (el) {
        disconnect(el[CTX]);
        delete el[CTX];
    },
};
